import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

const AcceptableUse = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <nav className="navbar px-6 py-4 bg-white/80 backdrop-blur-xl border-b sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo.png" alt="AutomateSEO.ai" className="h-11 w-auto" />
            <span className="text-2xl font-bold heading bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AutomateSEO.ai</span>
          </div>
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2 h-4 w-4" />Back to Home
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-10 h-10 text-blue-600" />
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Acceptable Use Policy</h1>
        </div>
        <p className="text-gray-600 mb-8">Last Updated: December 7, 2025</p>
        
        <div className="prose prose-lg max-w-none space-y-8">
          <section className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
            <p className="text-gray-700 leading-relaxed mb-3">
              <strong>Our Philosophy:</strong> AutomateSEO.ai is built to help legitimate businesses grow through ethical AI-powered marketing. Use our platform responsibly, respect others, and we'll all thrive together.
            </p>
            <p className="text-sm text-gray-600 mt-3 pt-3 border-t border-green-300">
              <strong>Trial & Refunds:</strong> New users get a 3-day free trial. Refunds available within 3 days of first payment. See Refund Policy for details.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">What This Policy Covers</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This Acceptable Use Policy defines how you may (and may not) use AutomateSEO.ai. Violating these guidelines may result in account suspension or termination. We've created these rules to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Protect the integrity of our platform</li>
              <li>Ensure fair use for all customers</li>
              <li>Comply with legal and regulatory requirements</li>
              <li>Maintain the quality and reputation of automated marketing</li>
              <li>Create a positive environment for ethical businesses</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">✅ Acceptable Uses</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              AutomateSEO.ai is designed for:
            </p>
            <div className="space-y-4">
              <div className="border-l-4 border-green-500 pl-6">
                <h3 className="text-xl font-semibold mb-2 text-gray-800">Legitimate Business Marketing</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Promoting your products, services, or business</li>
                  <li>Creating genuine, helpful content for your audience</li>
                  <li>Building authentic engagement on social media</li>
                  <li>Optimizing your website for search engines ethically</li>
                  <li>Managing your Google My Business profile legitimately</li>
                </ul>
              </div>

              <div className="border-l-4 border-green-500 pl-6">
                <h3 className="text-xl font-semibold mb-2 text-gray-800">Ethical SEO Practices</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>White-hat SEO optimization following Google guidelines</li>
                  <li>Creating original, valuable content for users</li>
                  <li>Building natural, contextual backlinks</li>
                  <li>Improving user experience and site performance</li>
                </ul>
              </div>

              <div className="border-l-4 border-green-500 pl-6">
                <h3 className="text-xl font-semibold mb-2 text-gray-800">Honest Social Media Marketing</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Posting authentic content to your own accounts</li>
                  <li>Engaging genuinely with your community</li>
                  <li>Running legitimate promotions and campaigns</li>
                  <li>Building real follower relationships</li>
                </ul>
              </div>

              <div className="border-l-4 border-green-500 pl-6">
                <h3 className="text-xl font-semibold mb-2 text-gray-800">Agency Services</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Reselling our platform with proper authorization</li>
                  <li>Managing multiple client accounts legitimately</li>
                  <li>Providing automation services to your customers</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">❌ Prohibited Uses</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The following activities are strictly forbidden:
            </p>

            <div className="space-y-6">
              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">🚫 Black-Hat SEO Tactics</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li><strong>Keyword Stuffing:</strong> Unnaturally cramming keywords into content</li>
                  <li><strong>Cloaking:</strong> Showing different content to search engines vs. users</li>
                  <li><strong>Link Schemes:</strong> Buying, selling, or exchanging links artificially</li>
                  <li><strong>Content Scraping:</strong> Copying content from other websites</li>
                  <li><strong>Hidden Text/Links:</strong> Using invisible text or deceptive link placement</li>
                  <li><strong>Doorway Pages:</strong> Creating low-quality pages just for rankings</li>
                  <li><strong>Private Blog Networks:</strong> Using fake blogs for backlinks</li>
                </ul>
              </div>

              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">🚫 Fake Reviews & Manipulation</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Generating fake customer reviews</li>
                  <li>Posting reviews for businesses you haven't used</li>
                  <li>Creating multiple fake accounts for reviews</li>
                  <li>Manipulating Google My Business ratings</li>
                  <li>Writing negative reviews for competitors</li>
                  <li>Offering incentives for positive reviews without disclosure</li>
                </ul>
              </div>

              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">🚫 Social Media Abuse</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Buying fake followers, likes, or engagement</li>
                  <li>Using bots to automate interactions (likes, comments, follows)</li>
                  <li>Posting spam or repetitive content</li>
                  <li>Creating fake accounts or impersonating others</li>
                  <li>Violating platform-specific terms (Instagram, Facebook, etc.)</li>
                  <li>Aggressive automation that violates platform rate limits</li>
                </ul>
              </div>

              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">🚫 Illegal or Harmful Content</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Promoting illegal products, services, or activities</li>
                  <li>Distributing malware, viruses, or harmful code</li>
                  <li>Sharing pirated software, content, or copyrighted material</li>
                  <li>Posting hate speech, discriminatory, or offensive content</li>
                  <li>Promoting violence, self-harm, or dangerous activities</li>
                  <li>Sharing explicit adult content or child exploitation material</li>
                  <li>Facilitating fraud, scams, or deceptive practices</li>
                </ul>
              </div>

              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">🚫 Platform Abuse</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Attempting to hack, breach, or compromise security</li>
                  <li>Reverse engineering our software or algorithms</li>
                  <li>Exceeding API rate limits or system resource quotas</li>
                  <li>Creating multiple accounts to avoid limits or fees</li>
                  <li>Reselling without proper authorization</li>
                  <li>Using automated tools to create accounts or abuse trials</li>
                  <li>Attempting to disrupt or damage our infrastructure</li>
                </ul>
              </div>

              <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
                <h3 className="text-xl font-semibold mb-3 text-gray-800">🚫 Data Misuse</h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Scraping data from our platform or other users</li>
                  <li>Selling or sharing access to your account</li>
                  <li>Collecting personal information without consent</li>
                  <li>Using data for purposes beyond your subscription</li>
                  <li>Violating GDPR, CCPA, or other privacy regulations</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Content Guidelines</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Content created through AutomateSEO.ai must:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="text-lg font-semibold mb-2 text-green-700">✅ DO</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Be original and valuable</li>
                  <li>• Accurately represent your business</li>
                  <li>• Follow platform-specific guidelines</li>
                  <li>• Respect copyrights and trademarks</li>
                  <li>• Include proper disclosures when required</li>
                  <li>• Provide honest information</li>
                </ul>
              </div>
              <div className="border-l-4 border-red-500 pl-4">
                <h3 className="text-lg font-semibold mb-2 text-red-700">❌ DON'T</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Plagiarize or copy competitor content</li>
                  <li>• Make false or exaggerated claims</li>
                  <li>• Use clickbait or misleading titles</li>
                  <li>• Include hidden affiliate links without disclosure</li>
                  <li>• Post duplicate content across multiple sites</li>
                  <li>• Violate advertising standards</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Enforcement and Consequences</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We take violations seriously. Depending on severity and frequency:
            </p>
            <div className="space-y-4">
              <div className="border-l-4 border-yellow-500 pl-6">
                <h3 className="text-lg font-semibold mb-2 text-gray-800">First Violation (Minor)</h3>
                <p className="text-gray-700">Warning email with explanation and 7-day correction period</p>
              </div>
              <div className="border-l-4 border-orange-500 pl-6">
                <h3 className="text-lg font-semibold mb-2 text-gray-800">Second Violation or Moderate First</h3>
                <p className="text-gray-700">Account suspension for 14 days, must acknowledge policy to reactivate</p>
              </div>
              <div className="border-l-4 border-red-500 pl-6">
                <h3 className="text-lg font-semibold mb-2 text-gray-800">Severe or Repeated Violations</h3>
                <p className="text-gray-700">Permanent account termination, no refund, possible legal action</p>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed mt-6">
              Severe violations (illegal content, hacking attempts, massive spam) result in immediate permanent termination without warning.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Reporting Violations</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you witness another user violating this policy:
            </p>
            <ol className="list-decimal pl-6 text-gray-700 space-y-2">
              <li>Document the violation (screenshots, URLs, dates)</li>
              <li>Email <strong>abuse@automateseo.ai</strong> with details</li>
              <li>We'll investigate within 48 hours</li>
              <li>Serious violations are escalated immediately</li>
            </ol>
            <p className="text-gray-700 leading-relaxed mt-4">
              Your report remains confidential. We appreciate community members who help maintain platform integrity.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Appeals Process</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If your account is suspended or terminated and you believe it was in error:
            </p>
            <ol className="list-decimal pl-6 text-gray-700 space-y-2">
              <li>Email <strong>appeals@automateseo.ai</strong> within 14 days</li>
              <li>Explain your case with supporting evidence</li>
              <li>Our compliance team reviews appeals within 5 business days</li>
              <li>Decisions are final after appeal review</li>
            </ol>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Acceptable Use Policy as platform capabilities evolve or new abuse patterns emerge. Users will be notified of significant changes via email 30 days in advance. Continued use after changes indicates acceptance.
            </p>
          </section>

          <section className="bg-blue-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Questions or Concerns?</h2>
            <p className="text-gray-700 mb-4">
              Not sure if something is allowed? Ask us before doing it:
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong> compliance@automateseo.ai<br/>
              <strong>Support:</strong> support@automateseo.ai<br/>
              <strong>Phone:</strong> +91 9810535398
            </p>
          </section>

          <section className="border-l-4 border-blue-500 pl-6">
            <p className="text-gray-700 leading-relaxed italic">
              "We're building AutomateSEO.ai for ethical businesses who want to grow through honest, valuable marketing. Let's keep it that way together." - AutomateSEO.ai Team
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AcceptableUse;